import { useEffect, useRef, useState } from 'react'
import { UserProfileUtils } from 'src/context/userProfileContext'
import clsx from 'clsx'
import { DateUtils } from 'src/utils/date'
import { UserAvatar } from 'src/components/userAvatar'
import { useTrackEvents } from 'src/context/userProfileContext/hooks'
import { ComponentName } from 'src/utils/datahubDefaultEvent'
import { useMentionUtils } from 'src/components/postCommentInput/hooks'
import ModerationTag from 'src/components/moderationTag/moderationTag'
import { useMediaQuery } from '@hotmart/space-lib/dist/hooks'
import { PostCommentInput } from 'src/components/postCommentInput'
import { FirstCommentBadge } from 'src/components/firstCommentBadge'
import { ScreenSizes } from 'src/styles'
import { ComponentNames } from 'src/utils/datahub/enums'
import { t } from 'src/i18n'
import { useUserProfile } from 'src/components/globalUserProfile'
import { DropdownOptions } from '../dropdownOptions'
import { PostRepliesComment } from '../postRepliesComment'
import { CommentType } from '../../postComments.types'
import { MediaPreview } from '../mediaPreview'
import { CommentReaction } from '../commentReaction'
import {
  AvatarWrapper,
  CommentContent,
  CommentText,
  Wrapper,
  AuthorName
} from './postCommentItem.styles'
import { CommentProps, PostCommentItemProps } from './postCommentItem.types'

const PostCommentItem = ({
  postId,
  authorRole,
  ownerCollectionId,
  comment,
  isReply,
  onReloadComments,
  canUserRespondToComment,
  enableEditBadge,
  onEditComment
}: PostCommentItemProps) => {
  const container = useRef<HTMLDivElement>(null)
  const [openInputAnswer, setOpenInputAnswer] = useState(false)
  const [isEdited, setIsEdited] = useState(false)

  const { loggedUserUcode, onOpen: onOpenUserProfile } = useUserProfile()
  const { sendOpenProfileEvent } = useTrackEvents({ loggedUserUcode })
  const isMobile = useMediaQuery(`(max-width: ${ScreenSizes.md})`)
  const { convertMentionToHTML, renderMentionHTMLTag } = useMentionUtils()

  const commentInputParentCommentId = isReply ? comment.parentCommentId : comment.id
  const commentDate = DateUtils.compareDates(comment.date)
  const canDeleteComment = Boolean(comment.canDelete)
  const enabledOpenUserProfile = Boolean(comment?.authorUcode)
  const replyCommentInitialValue = renderMentionHTMLTag({
    name: comment.author,
    ucode: comment.authorUcode
  })

  const handleOpenUserProfile = () => {
    if (enabledOpenUserProfile) {
      // onOpenUserProfile(event, comment.authorUcode)
      onOpenUserProfile(comment.authorUcode)
      sendOpenProfileEvent({
        componentName: ComponentName.POST_COMMENT,
        userUcode: comment.authorUcode
      })
    }
  }

  const convertCommentToHTML = (comment: CommentProps) => {
    return convertMentionToHTML({
      text: comment?.message,
      mentions: comment?.mentions?.map((mention) => ({
        tag: mention?.tag,
        failed: Boolean(mention?.failed),
        failureReason: mention?.failureReason,
        ucode: mention?.metadata?.ucode,
        name: mention?.metadata?.name
      }))
    })
  }

  const handleReplyComment = () => {
    onReloadComments()
    setOpenInputAnswer(false)
  }

  const profileActionProps = enabledOpenUserProfile
    ? {
        role: 'button',
        tabIndex: 0,
        onClick: handleOpenUserProfile,
        onKeyDown: handleOpenUserProfile
      }
    : {}

  const isModerator = comment.authorMemberType === 'MODERATOR'

  const handleEditComment = () => {
    setIsEdited(true)
    onEditComment?.(comment)
  }

  useEffect(() => {
    const elementsToOpenProfile = container.current?.querySelectorAll(
      '[data-action="open-profile"]'
    )

    const callback = ({ ucode }) => {
      onOpenUserProfile(ucode)
      sendOpenProfileEvent({
        componentName: ComponentName.POST_COMMENT,
        userUcode: ucode,
        accessFromMention: true
      })
    }

    const removeListener = UserProfileUtils.openEventListener({
      elements: elementsToOpenProfile,
      componentName: ComponentName.POST_COMMENT,
      callback
    })

    return removeListener
  }, [])

  return (
    <>
      <Wrapper
        ref={container}
        data-test="comment-item"
        className="_d-flex _align-items-start _w-full _mt-5 _pb-5 _border-bottom _border-gray-200 _flex-column"
      >
        <div className="_d-flex _align-items-center _w-full">
          <AvatarWrapper
            className={clsx('_flex-shrink-0 _mt-0', {
              '_cursor-pointer': enabledOpenUserProfile
            })}
            {...profileActionProps}
          >
            <UserAvatar src={comment?.authorImageFinalUrl} alt={comment.author} small />
          </AvatarWrapper>

          <div className="_d-flex _align-items-center _justify-content-between _w-full _ml-4">
            <div>
              <div className="_text-gray-900 _text-1 _mb-0 _d-flex _align-items-center _justify-content-center">
                <AuthorName
                  className={clsx('_mr-2', {
                    '_cursor-pointer': enabledOpenUserProfile,
                    '_text-decoration-underline': enabledOpenUserProfile
                  })}
                  {...profileActionProps}
                >
                  {comment.author}
                </AuthorName>
                {isModerator && <ModerationTag className="_mr-2" />}
                <span>{commentDate}</span>
                {!isMobile && comment?.first && <FirstCommentBadge className="_ml-3" />}
              </div>

              {isMobile && comment?.first && <FirstCommentBadge className="_mt-1" />}
            </div>

            <div className="_ml-auto _d-flex _align-items-center">
              {enableEditBadge && isEdited && (
                <span className="_text-gray-500 _text-1 _mr-4" data-test="edited-badge">
                  {t('comments.badge_edited')}
                </span>
              )}
              {canDeleteComment && (
                <DropdownOptions
                  comment={comment}
                  ownerCollectionId={ownerCollectionId}
                  onReloadComments={onReloadComments}
                  onEditComment={handleEditComment}
                />
              )}
            </div>
          </div>
        </div>

        <CommentContent className={clsx({ '_pt-3': isMobile })}>
          {comment?.message && (
            <CommentText dangerouslySetInnerHTML={{ __html: convertCommentToHTML(comment) }} />
          )}

          <MediaPreview
            postId={postId}
            comment={comment}
            arrivedFrom={ComponentNames.POST_COMMENT}
          />

          <CommentReaction
            commentId={comment.id}
            reactionsCount={comment.reactionsCount}
            userReaction={comment.userReaction}
            openInputAnswer={openInputAnswer}
            setOpenInputAnswer={setOpenInputAnswer}
            onSuccess={onReloadComments}
            canUserRespondToComment={canUserRespondToComment}
          />

          {openInputAnswer && (
            <PostCommentInput
              value={replyCommentInitialValue}
              allowMedias={[CommentType.IMAGE, CommentType.DOCUMENT]}
              className="_mt-5"
              isAnswer
              parentCommentId={commentInputParentCommentId}
              idPost={postId}
              enableMentions
              enableBreakLine
              authorRole={authorRole}
              onSuccess={handleReplyComment}
            />
          )}

          <PostRepliesComment
            comment={comment}
            ownerCollectionId={ownerCollectionId}
            canUserRespondToComment={canUserRespondToComment}
          />
        </CommentContent>
      </Wrapper>
    </>
  )
}

export default PostCommentItem
